import AudioWave from '@react-spectrum/s2/icons/AudioWave';
import Comment from '@react-spectrum/s2/icons/Comment';
import Email from '@react-spectrum/s2/icons/Email';
import Image from '@react-spectrum/s2/icons/Image';
import Promote from '@react-spectrum/s2/icons/Promote';
import Search from '@react-spectrum/s2/icons/Search';
import type { ReactNode } from 'react';

// Spectrum icons OR a local one taking `className`: the For You pill uses
// AISparklesIcon. PillChip renders `<pill.Icon />` with no props, so both are fine.
type IconComponent =
	| typeof Image
	| ((props: { className?: string }) => ReactNode);

export interface PillOption {
	label: string;
	message: string;
}

export interface PillSpec {
	label: string;
	Icon: IconComponent;
	options: PillOption[];
}

export const PILLS: PillSpec[] = [
	{
		label: 'Instagram Content',
		Icon: Image,
		options: [
			{
				label: 'Build my content strategy and create my first posts',
				message:
					"Look at my Instagram account. Audit what I've been posting, how it's performing, and where the gaps are. Don't ask me questions — just tell me what you see and what's missing. Then define 3 content pillars that make sense for my business and audience. For each pillar, create two carousels optimized for saves and DM shares. Each carousel should have a clear save trigger, a specific share prompt, and be designed for one person, not a broad audience.",
			},
		],
	},
	{
		label: 'Automate Engagement',
		Icon: Comment,
		options: [
			{
				label: 'Turn comments into product sales',
				message:
					"I want to sell my products directly through Instagram comments and DMs. Look at what I sell and create a studio-quality product showcase carousel that puts my best products front and center. For each product, invite followers to comment if they're interested. When someone comments, DM them a direct link to that product — no extra steps, no friction. Tell me which products are getting the most comment interest so I know what's resonating.",
			},
		],
	},
	{
		label: 'Market Research',
		Icon: Search,
		options: [
			{
				label: 'Understand my pricing and packaging',
				message:
					"Look at my business, what I charge, and how I package my offers. Then research what others in my niche are charging for similar things. Don't just tell me what they charge — tell me where I'm underpriced, where I'm leaving money on the table, and what packaging structure is working best in my market right now. Give me a clear recommendation on what to change and why.",
			},
		],
	},
	{
		label: 'Email Marketing',
		Icon: Email,
		options: [
			{
				label: 'Run a promo campaign for an upcoming holiday',
				message:
					"Look at my business and identify the next holiday or seasonal moment that's actually relevant to what I sell — don't just pick the next date on the calendar. Recommend an offer to run for that moment and tell me why that offer fits that audience at that time. Then build a 3-email sequence: a warmup that builds anticipation, a main push with the full offer, and a last-chance email sent the morning the offer ends. Each email should feel like a natural next step, not a separate blast. When I approve the sequence, schedule and send each email automatically at the right time.",
			},
		],
	},
	{
		label: 'Paid Ads',
		Icon: Promote,
		options: [
			{
				label: 'Create a polished product ad',
				message:
					'Look at my business and create a studio-quality ad image that makes my product or service look premium. Clean composition, professional finish, brand-consistent. This is the ad that builds trust and makes someone feel good about buying. Give me the image plus a headline and primary text that matches the polish of the visual.',
			},
		],
	},
	{
		label: 'Spot Trends',
		Icon: AudioWave,
		options: [
			{
				label: 'See what people are talking about right now',
				message:
					"Monitor Reddit for conversations gaining momentum in my niche right now. Don't look for complaints — look for what people are excited about, asking for more of, and recommending to each other. Tell me what's rising, why it matters for my business, and what I should create or offer to ride that momentum.",
			},
		],
	},
];

export const EMPTY_STATE_PROMPTS = PILLS.flatMap((p) =>
	p.options.map((o) => o.label),
);
